import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';
import SEO from '../components/common/SEO';
import Pagination from '../components/common/Pagination';
import apiClient from '../api/apiClient';
import { formatDate, getLocalDateString } from '../utils/dateUtils';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import './MyEnquiries.css';

const MyEnquiries = () => {
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isUnauthorized, setIsUnauthorized] = useState(false);

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters state
    const [filters, setFilters] = useState({
        search: '',
        category: '',
        startDate: '',
        endDate: ''
    });

    const checkToken = () => {
        let token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (!token) {
            try {
                const sessionStr = localStorage.getItem('visitorUser') || localStorage.getItem('userInfo');
                if (sessionStr) {
                    const parsed = JSON.parse(sessionStr);
                    token = parsed?.token || parsed?.user?.token || parsed?.user?.user?.token;
                }
            } catch (e) { }
        }
        return token;
    };

    const fetchMyEnquiries = useCallback(async () => {
        setLoading(true);
        setError('');
        setIsUnauthorized(false);

        const activeToken = checkToken();
        if (!activeToken) {
            setLoading(false);
            setIsUnauthorized(true);
            setError('Please sign in to your Mangoloops account to view your enquiry history.');
            return;
        }

        try {
            const params = {
                page,
                limit,
                ...filters
            };

            Object.keys(params).forEach(key => {
                if (!params[key]) delete params[key];
            });

            const queryParams = new URLSearchParams(params);
            const response = await apiClient.get(`/enquiry/my-enquiries?${queryParams}`);

            if (response.data?.success) {
                setEnquiries(response.data.data || []);
                setTotalPages(response.data.pagination?.totalPages || 1);
                setTotalRecords(response.data.pagination?.total || 0);
            }
        } catch (err) {
            console.error('Error fetching my enquiries:', err);
            if (err.response?.status === 401) {
                setIsUnauthorized(true);
                setError('Your login session has expired or is invalid. Please sign in again.');
            } else {
                setError(err.response?.data?.message || 'Failed to load your enquiries. Please try refreshing the page.');
            }
        } finally {
            setLoading(false);
        }
    }, [page, limit, filters]);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchMyEnquiries();
        }, 300);
        return () => clearTimeout(handler);
    }, [fetchMyEnquiries]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setPage(1);
    };

    const getCategoryBadgeClass = (category) => {
        switch (category) {
            case 'holidays': return 'category-badge-holidays';
            case 'hotels': return 'category-badge-hotels';
            case 'cab':
            case 'cabs': return 'category-badge-cabs';
            case 'tirth_yatra': return 'category-badge-tirth_yatra';
            case 'medical_tourism':
            case 'medical_tourism_doctor':
            case 'medical_tourism_hospital': return 'category-badge-medical_tourism';
            default: return 'bg-secondary text-white';
        }
    };

    const formatCategoryName = (category) => {
        if (!category) return 'General';
        if (category === 'tirth_yatra') return 'Tirth Yatra';
        if (category.startsWith('medical_tourism')) return 'Medical Tourism';
        return category.charAt(0).toUpperCase() + category.slice(1);
    };

    return (
        <React.Fragment>
            <SEO
                title="My Enquiries | Mangoloops"
                description="View all listing enquiries and contact history on Mangoloops."
            />
            <Header />

            <main className="my-enquiries-page">
                <div className="container">
                    {/* Hero Header */}
                    <div className="my-enquiries-hero text-center">
                        <h2><i className="fas fa-paper-plane me-2 fs-4 text-warning"></i> My Enquiries</h2>
                        <p className="mb-0">
                            Track all listings you have enquired about along with vendor details and timestamps.
                        </p>
                    </div>

                    {/* Filter Bar */}
                    <div className="my-enquiries-filter-bar p-3 mb-4">
                        <div className="row g-3 align-items-center">
                            <div className="col-lg-5 col-md-12">
                                <div className="input-group">
                                    <span className="input-group-text bg-white text-muted border-end-0">
                                        <i className="fas fa-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 ps-0"
                                        placeholder="Search by listing name or keyword..."
                                        name="search"
                                        value={filters.search}
                                        onChange={handleFilterChange}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <select
                                    className="form-select"
                                    name="category"
                                    value={filters.category}
                                    onChange={handleFilterChange}
                                >
                                    <option value="">All Categories</option>
                                    <option value="holidays">Holidays</option>
                                    <option value="hotels">Hotels</option>
                                    <option value="cab">Cabs</option>
                                    <option value="tirth_yatra">Tirth Yatra</option>
                                    <option value="medical_tourism">Medical Tourism</option>
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-5">
                                <Flatpickr
                                    className="form-control bg-white"
                                    placeholder="Filter by Date Range"
                                    value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : (filters.startDate ? [filters.startDate] : [])}
                                    options={{
                                        mode: "range",
                                        dateFormat: "Y-m-d"
                                    }}
                                    onChange={(dates) => {
                                        if (dates.length === 2) {
                                            setFilters(prev => ({
                                                ...prev,
                                                startDate: getLocalDateString(dates[0]),
                                                endDate: getLocalDateString(dates[1])
                                            }));
                                            setPage(1);
                                        } else if (dates.length === 0) {
                                            setFilters(prev => ({
                                                ...prev,
                                                startDate: '',
                                                endDate: ''
                                            }));
                                            setPage(1);
                                        }
                                    }}
                                />
                            </div>
                            <div className="col-lg-1 col-md-1 text-md-end">
                                {(filters.search || filters.category || filters.startDate) && (
                                    <button
                                        className="btn btn-outline-secondary btn-sm w-100"
                                        title="Clear Filters"
                                        onClick={() => {
                                            setFilters({ search: '', category: '', startDate: '', endDate: '' });
                                            setPage(1);
                                        }}
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    {loading ? (
                        <div className="card my-enquiries-card py-5 text-center">
                            <div className="spinner-border text-primary mx-auto mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted mb-0">Fetching your enquiries...</p>
                        </div>
                    ) : isUnauthorized ? (
                        <div className="card my-enquiries-card p-5 text-center my-4">
                            <div className="py-3">
                                <div className="bg-warning-subtle text-warning d-inline-flex p-3 rounded-circle mb-3">
                                    <i className="fas fa-lock fa-2x"></i>
                                </div>
                                <h4 className="fw-bold">Sign In Required</h4>
                                <p className="text-muted col-md-6 mx-auto mb-4">
                                    {error}
                                </p>
                                <Link to="/login" className="btn btn-primary px-4 py-2 rounded-pill shadow-sm">
                                    <i className="fas fa-sign-in-alt me-2"></i> Sign In to Account
                                </Link>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="card my-enquiries-card p-4 text-center my-4 border-danger">
                            <i className="fas fa-exclamation-triangle fa-2x text-danger mb-2"></i>
                            <h5 className="text-danger fw-bold">{error}</h5>
                            <button className="btn btn-outline-primary btn-sm mx-auto mt-2 rounded-pill px-4" onClick={fetchMyEnquiries}>
                                <i className="fas fa-redo me-1"></i> Try Again
                            </button>
                        </div>
                    ) : enquiries.length > 0 ? (
                        <div className="enquiries-container">
                            {/* Desktop View Table (screen >= 992px) */}
                            <div className="d-none d-lg-block">
                                <div className="card my-enquiries-card">
                                    <div className="table-responsive">
                                        <table className="table my-enquiries-table">
                                            <thead>
                                                <tr>
                                                    <th>Listing Details</th>
                                                    <th>Category</th>
                                                    <th>Vendor Details</th>
                                                    <th>Vendor Contact</th>
                                                    <th>Enquiry Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {enquiries.map((enquiry) => (
                                                    <tr key={enquiry.id}>
                                                        {/* Listing Title & Link */}
                                                        <td>
                                                            <div>
                                                                {enquiry.listing_link ? (
                                                                    <Link to={enquiry.listing_link} className="enquiry-listing-title">
                                                                        {enquiry.listing_title} <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                                                                    </Link>
                                                                ) : (
                                                                    <span className="enquiry-listing-title">{enquiry.listing_title}</span>
                                                                )}
                                                            </div>
                                                            {enquiry.message && (
                                                                <div className="text-muted small mt-1 text-truncate" style={{ maxWidth: '320px' }}>
                                                                    {enquiry.message}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Category */}
                                                        <td>
                                                            <span className={`category-pill ${getCategoryBadgeClass(enquiry.category)}`}>
                                                                {formatCategoryName(enquiry.category)}
                                                            </span>
                                                        </td>

                                                        {/* Vendor Details */}
                                                        <td>
                                                            {enquiry.vendor_link ? (
                                                                <Link to={enquiry.vendor_link} className="vendor-name-link">
                                                                    <i className="fas fa-store text-primary me-1"></i> {enquiry.vendor_name}
                                                                </Link>
                                                            ) : (
                                                                <span className="fw-semibold text-dark">
                                                                    <i className="fas fa-store text-muted me-1"></i> {enquiry.vendor_name}
                                                                </span>
                                                            )}
                                                        </td>

                                                        {/* Vendor Phone & Email */}
                                                        <td>
                                                            <div className="d-flex flex-column gap-1">
                                                                {enquiry.vendor_phone && enquiry.vendor_phone !== 'N/A' ? (
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <a href={`tel:${enquiry.vendor_phone}`} className="contact-pill-link">
                                                                            <i className="fas fa-phone-alt text-success"></i> {enquiry.vendor_phone}
                                                                        </a>
                                                                        <a
                                                                            href={`https://wa.me/${enquiry.vendor_phone.replace(/\D/g, '')}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-success fs-5"
                                                                            title="Chat on WhatsApp"
                                                                        >
                                                                            <i className="fab fa-whatsapp"></i>
                                                                        </a>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted small">Phone: N/A</span>
                                                                )}

                                                                {enquiry.vendor_email && enquiry.vendor_email !== 'N/A' ? (
                                                                    <a href={`mailto:${enquiry.vendor_email}`} className="contact-pill-link text-truncate" style={{ maxWidth: '200px' }}>
                                                                        <i className="fas fa-envelope text-primary"></i> {enquiry.vendor_email}
                                                                    </a>
                                                                ) : null}
                                                            </div>
                                                        </td>

                                                        {/* Enquiry Date */}
                                                        <td>
                                                            <span className="enquiry-time-badge">
                                                                <i className="far fa-clock me-1"></i> {formatDate(enquiry.created_at)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile View Cards (screen < 992px) */}
                            <div className="d-lg-none">
                                {enquiries.map((enquiry) => (
                                    <div key={enquiry.id} className="enquiry-item-card">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <span className={`category-pill ${getCategoryBadgeClass(enquiry.category)}`}>
                                                {formatCategoryName(enquiry.category)}
                                            </span>
                                            <span className="enquiry-time-badge">
                                                <i className="far fa-clock me-1"></i> {formatDate(enquiry.created_at)}
                                            </span>
                                        </div>

                                        <div className="mb-2">
                                            {enquiry.listing_link ? (
                                                <Link to={enquiry.listing_link} className="enquiry-listing-title fs-6">
                                                    {enquiry.listing_title} <i className="fas fa-external-link-alt ms-1 small text-primary"></i>
                                                </Link>
                                            ) : (
                                                <span className="enquiry-listing-title fs-6">{enquiry.listing_title}</span>
                                            )}
                                        </div>

                                        <div className="bg-light p-2.5 rounded-3 mb-2 small">
                                            <div className="text-muted mb-1">Vendor:</div>
                                            {enquiry.vendor_link ? (
                                                <Link to={enquiry.vendor_link} className="vendor-name-link fw-bold">
                                                    <i className="fas fa-store text-primary me-1"></i> {enquiry.vendor_name}
                                                </Link>
                                            ) : (
                                                <span className="fw-bold text-dark">
                                                    <i className="fas fa-store text-muted me-1"></i> {enquiry.vendor_name}
                                                </span>
                                            )}
                                        </div>

                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            {enquiry.vendor_phone && enquiry.vendor_phone !== 'N/A' && (
                                                <a href={`tel:${enquiry.vendor_phone}`} className="btn btn-outline-success btn-sm rounded-pill px-3">
                                                    <i className="fas fa-phone-alt me-1"></i> Call
                                                </a>
                                            )}
                                            {enquiry.vendor_phone && enquiry.vendor_phone !== 'N/A' && (
                                                <a
                                                    href={`https://wa.me/${enquiry.vendor_phone.replace(/\D/g, '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-success btn-sm rounded-pill px-3"
                                                >
                                                    <i className="fab fa-whatsapp me-1"></i> WhatsApp
                                                </a>
                                            )}
                                            {enquiry.vendor_email && enquiry.vendor_email !== 'N/A' && (
                                                <a href={`mailto:${enquiry.vendor_email}`} className="btn btn-outline-primary btn-sm rounded-pill px-3">
                                                    <i className="fas fa-envelope me-1"></i> Email
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 gap-3">
                                    <div className="text-muted small">
                                        Showing {enquiries.length} of {totalRecords} enquiries
                                    </div>
                                    <Pagination
                                        currentPage={page}
                                        totalPages={totalPages}
                                        onPageChange={(p) => setPage(p)}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="card my-enquiries-card p-5 text-center my-4">
                            <div className="py-4">
                                <i className="fas fa-search-location fa-3x text-muted mb-3"></i>
                                <h4 className="fw-bold">No Enquiries Found</h4>
                                <p className="text-muted col-md-7 mx-auto mb-4">
                                    You have not submitted any listing enquiries yet. Explore our holiday packages, hotels, cabs, or yatras to get started!
                                </p>
                                <Link to="/holidays" className="btn btn-primary px-4 py-2 rounded-pill shadow-sm">
                                    Browse Holiday Packages
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </React.Fragment>
    );
};

export default MyEnquiries;
